import { Incognito, Network, Users } from "components/icons/generated";
import { IconComponent } from "types/Icon";
import { BillingTier } from "types/generated";

// Tiers shown as upgrade cards, in display order. Excludes V5Free (the floor), V5StarterPlus_2 (shown as
// Starter +), and V5Strategic (top / sales-only, not promoted separately — covered by Enterprise +).
export const UPGRADE_MARKETING_TIERS: BillingTier[] = [
  BillingTier.V5Starter,
  BillingTier.V5StarterPlus,
  BillingTier.V5Business,
  BillingTier.V5Enterprise,
  BillingTier.V5EnterprisePlus,
];

type BillingTierDetails = {
  icon: IconComponent;
  text: string;
};

export const BASE_INFO_BY_TIER: Partial<
  Record<BillingTier, { price: string; title?: string; description: string }>
> = {
  [BillingTier.V5StarterPlus]: {
    title: "Annual plan starting from",
    price: "$20,000 / y",
    description:
      "For growing teams looking to optimize team workflows with infrastructure automation.",
  },
  [BillingTier.V5Business]: {
    title: "Annual plan",
    price: "Custom pricing",
    description:
      "For teams looking to orchestrate infrastructure provisioning, configuration, and collaboration.",
  },
  [BillingTier.V5Enterprise]: {
    title: "Annual plan",
    price: "Custom pricing",
    description:
      "For companies that need Enterprise-level infrastructure orchestration and governance.",
  },
  [BillingTier.V5EnterprisePlus]: {
    title: "Annual plan",
    price: "Custom pricing",
    description:
      "For companies that need SaaS/Self-hosted with special security, compliance, and support.",
  },
};

export const DETAILS_BY_TIER: Partial<Record<BillingTier, BillingTierDetails[]>> = {
  [BillingTier.V5Starter]: [
    { icon: Users, text: "10 Users" },
    { icon: Network, text: "2 Public Workers" },
  ],
  [BillingTier.V5StarterPlus]: [
    { icon: Users, text: "Unlimited Users" },
    { icon: Incognito, text: "1 Private Worker" },
  ],
  [BillingTier.V5Business]: [
    { icon: Users, text: "Unlimited Users" },
    { icon: Incognito, text: "3 Private Workers" },
  ],
  [BillingTier.V5Enterprise]: [
    { icon: Users, text: "Unlimited Users" },
    { icon: Incognito, text: "5-30 Private Workers" },
  ],
  [BillingTier.V5EnterprisePlus]: [
    { icon: Users, text: "Unlimited Users" },
    { icon: Incognito, text: "12-30 Private Workers" },
  ],
};

// Tooltips are kept on features carried over from V4; new V5 features ship without one. Decided with
// product: keep the existing tooltips for clarity, don't add new ones to avoid the upkeep — so a tier
// can mix items with and without a tooltip.
export const ADDITIONAL_FEATURES_BY_TIER: Partial<
  Record<BillingTier, { text: string; infoTooltip?: string }[]>
> = {
  [BillingTier.V5Starter]: [
    {
      text: "OIDC Integrations",
      infoTooltip: "Other tool/cloud provider integrations with managed OpenID Connect.",
    },
    {
      text: "Private Module Registry",
      infoTooltip: "Gives you the ability to host, share, and test your modules.",
    },
    {
      text: "Webhooks",
      infoTooltip: "Send notifications for different events to an HTTP endpoint of your choice.",
    },
    {
      text: "Policy as Code Engine",
      infoTooltip: "Enhance your workflow with plan & approval policies.",
    },
    {
      text: "Notifications",
      infoTooltip:
        "Seamless and bi-directional integration between Spacelift and Slack, Notification inbox, Webhooks, and Microsoft Teams.",
    },
  ],
  [BillingTier.V5StarterPlus]: [
    { text: "Drift Detection", infoTooltip: "Detect drift and optionally remediate it." },
  ],
  [BillingTier.V5Business]: [
    {
      text: "Blueprints",
      infoTooltip:
        "Templates for a stack and its configuration (even policy, contexts, and cloud integration attachments).",
    },
    { text: "Templates" },
    {
      text: "Advanced Scheduling",
      infoTooltip: "Scheduled tasks and stack auto deletion based on cron.",
    },
    {
      text: "Private Provider Registry",
      infoTooltip: "Unlocks the ability to host and share providers.",
    },
    {
      text: "Targeted Replans",
      infoTooltip: "Enables you to have targeted runs in your workflow.",
    },
    {
      text: "Dedicated Slack Support Channel",
      infoTooltip: "Shared Slack channel between you and your dedicated CSM.",
    },
    { text: "Dedicated Customer Success Manager" },
    { text: "Silver Support + CSM" },
  ],
  [BillingTier.V5Enterprise]: [
    {
      text: "Concurrent-VCS Connections",
      infoTooltip: "Ability to create multiple integrations per VCS.",
    },
    {
      text: "Audit Trail",
      infoTooltip: "Unlocks the ability to create audit trails via configurable webhooks.",
    },
    {
      text: "SSO Integration with SAML 2.0",
      infoTooltip: "Supports all SAML 2.0-compliant identity providers.",
    },
    { text: "OIDC API Keys", infoTooltip: "Ability to use more secure OIDC-based API keys." },
    { text: "Internal source control system", infoTooltip: "Unreachable over the internet" },
    { text: "Enhanced Onboarding Support" },
    { text: "Gold Support + CSM" },
    { text: "MFA" },
  ],
  [BillingTier.V5EnterprisePlus]: [
    { text: "Self-hosted" },
    { text: "FedRAMP" },
    { text: "On-prem / Airgapped" },
    { text: "Gold Support + CSM + Executive Escalation" },
    { text: "Unmanaged Resources (coming soon)" },
  ],
};

// The promo box rendered on a card, by tier. Title differs by intent: a self-serve upgrade (Starter +)
// reads "Upgrade available:", a contact-sales extension (Enterprise +) reads "Available upon request:".
export const UPGRADE_BOX_BY_TIER: Partial<Record<BillingTier, { title: string; items: string[] }>> =
  {
    [BillingTier.V5StarterPlus]: {
      title: "Upgrade available:",
      items: ["Additional Private Worker"],
    },
    [BillingTier.V5EnterprisePlus]: {
      title: "Available upon request:",
      items: ["100+ Workers", "Named TAM", "24/7 Escalation", "Single Tenant"],
    },
  };
